/**
 * `useDeleteFolder` mutation hook.
 *
 * Wraps the raw `deleteProjectFolder` service fn. Cache
 * invalidation: invalidates every folder query under the project (using the
 * `['project', projectId, 'folders']` prefix) — broader than create's
 * targeted invalidation because a delete potentially affects the parent
 * folder's children listing AND the root view.
 *
 * `onSuccess` is awaited by the caller: the invalidated folder list is still
 * observed by the rail at this point, so awaiting it here means the refetch
 * lands before the mutation settles. Skipping the wait let a caller navigate
 * off the deleted folder while the list still held it, landing back on the
 * folder it had just deleted — "not found" instead of the empty state.
 *
 * `onDeleted` is a mutation-level callback ON PURPOSE. The surface that
 * deletes a folder is drawn by that folder — the pane header's menu — so it
 * unmounts the moment the folder is gone, and TanStack drops the callbacks
 * passed to `mutate` once their observer has no listeners
 * (`MutationObserver.#notify` checks `hasListeners()`). Handing leaving the
 * folder to a per-call `onSuccess` therefore lost it exactly when it was
 * needed, stranding the deleter on the "folder not found" pane.
 *
 * Side effects: NONE beyond that. Toast and confirm-dialog UX belong to the
 * caller (FolderActionsMenu owns the DeleteConfirmationModal + success toast —
 * pulling those into the mutation would couple the hook to UI choices that
 * vary by surface).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BRIEFS_QUERY_KEYS } from '@/entities/brief';
import type { ProjectFoldersResponse } from '@/entities/project-folder';
import { deleteProjectFolder } from './project-folders.api';

export { deleteProjectFolder };

interface UseDeleteFolderOptions {
  projectId: string;
  /**
   * Runs once the folder is gone, before any cache work, so the caller decides
   * where to go while the tree it is leaving is still the one on screen.
   */
  onDeleted?: (folderId: string) => void;
}

export function useDeleteFolder(options: UseDeleteFolderOptions) {
  const queryClient = useQueryClient();
  const { projectId, onDeleted } = options;

  return useMutation({
    mutationFn: (folderId: string) => deleteProjectFolder(projectId, folderId),
    onSuccess: async (_result, folderId) => {
      onDeleted?.(folderId);

      // The rail draws its rows straight from these lists, and the refetch
      // below is a round trip away: without this the folder just deleted stays
      // in the tree, and a route that reads the list to pick a folder can
      // still pick it.
      queryClient.setQueriesData<ProjectFoldersResponse>(
        { queryKey: ['project', projectId, 'folders', 'list'] },
        (page) =>
          page?.items.some((folder) => folder.id === folderId)
            ? {
                ...page,
                items: page.items.filter((folder) => folder.id !== folderId),
                total: Math.max(0, page.total - 1),
              }
            : page,
      );

      await queryClient.invalidateQueries({
        queryKey: ['project', projectId, 'folders'],
      });

      // A brief reports the folders it is delivered into, and one of them may
      // be the folder just deleted — the panel there renders each as a link.
      // Unconditional because nothing here says whether the subtree held a
      // brief, and the answer left the cache with the folder.
      void queryClient.invalidateQueries({ queryKey: BRIEFS_QUERY_KEYS.all });
    },
  });
}
